import { RELOAD_LABEL, type EntryView } from "./entry-steps.ts";

/**
 * Честный экран входа: список шагов и их состояния словом (раздел 4.4
 * `docs/room-preflight-design.md`).
 *
 * ⚠️ **Один компонент, два места.** Пока комнаты нет, его рисует `LessonPage`
 * вместо серой строки «Открываем урок…». Как только чанк приехал, комната
 * монтируется, и тот же компонент едет ОВЕРЛЕЕМ поверх неё, досказывая шаг про
 * доску: состояние доски знает только `Whiteboard`, а он монтируется после
 * `Suspense`, то есть экран, стоящий выше, про этот шаг не узнал бы никогда.
 *
 * ⚠️ **Лениво этот компонент не грузится никогда, и это не оплошность.** Он
 * нужен ровно в ту минуту, когда чанк комнаты ещё едет, то есть ленивая
 * загрузка означала бы «показать экран загрузки после того, как всё
 * загрузилось». Поэтому он живёт в стартовом чанке и обязан быть маленьким: ни
 * одного импорта из комнаты, ни одного из LiveKit, ни одного ассета.
 *
 * ⚠️ **Страница не перезагружается сама никогда**, ни по таймеру, ни по
 * возвращению фокуса. Перезагружает только человек нажатием, и кнопка
 * появляется только тогда, когда прогресс действительно встал (разбор в
 * `entry-steps.ts`). Это то же правило, что у полосы свежести сборки, и по той
 * же причине: перезагрузка посреди занятия это ровно та поломка, против которой
 * построен продукт.
 */
export function RoomLoading(props: { view: EntryView; place: "page" | "overlay" }) {
  // ⚠️ role="status" стоит на самом списке, а не на обёртке: страничный вид это
  // <main>, и роль на нём стёрла бы ориентир страницы для читалки экрана
  const box = (
    <div className="room__loading-box" role="status" aria-live="polite">
      <h2>{props.view.title}</h2>
      <ul>
        {props.view.rows.map((row) => (
          <li key={row.step}>
            <span>{row.label}</span>
            <span className="room__loading-word">{row.word}</span>
          </li>
        ))}
      </ul>
      {props.view.hint === null ? null : <p className="muted">{props.view.hint}</p>}
      {props.view.reload ? (
        <button type="button" className="btn" onClick={() => window.location.reload()}>
          {RELOAD_LABEL}
        </button>
      ) : null}
    </div>
  );

  // Страничный вид берёт готовую раскладку лобби: это тот же экран, что «Урок
  // не найден» и «Занятие ещё не началось», и уезжать он от них не должен
  if (props.place === "page") {
    return (
      <main className="lobby">
        {box}
      </main>
    );
  }

  return (
    <div className="room__loading">
      {box}
    </div>
  );
}
